import { useState, useEffect, useCallback } from 'react';
import { getProjectAttachments } from '../services/customerInvoiceAttachmentService';
import type { CustomerInvoiceAttachmentView } from '../types';

interface UseCustomerInvoiceAttachmentsOptions {
  projectId: string | null;
  autoFetch?: boolean;
}

interface UseCustomerInvoiceAttachmentsReturn {
  attachments: CustomerInvoiceAttachmentView[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCustomerInvoiceAttachments({
  projectId,
  autoFetch = true,
}: UseCustomerInvoiceAttachmentsOptions): UseCustomerInvoiceAttachmentsReturn {
  const [attachments, setAttachments] = useState<CustomerInvoiceAttachmentView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAttachments = useCallback(async () => {
    if (!projectId) {
      setAttachments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getProjectAttachments(projectId);
      setAttachments(data);
    } catch (err) {
      console.error('Error fetching customer invoice attachments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load attachments');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (autoFetch) {
      fetchAttachments();
    }
  }, [autoFetch, fetchAttachments]);

  return {
    attachments,
    isLoading,
    error,
    refetch: fetchAttachments,
  };
}
